import config from '../config.js';
import { analyticsEnabled, captureEvent } from './analytics.js';

/*
 * Performer events for PostHog (ENG-888). The events themselves are listed in
 * the header of `analytics.js`; this file builds their common properties.
 *
 * Every event carries `band_id`, plus `show_live` (and `show_id` while a show
 * is live). A caller that knows the show passes it (`show`); otherwise it is
 * read from `GET /public/bands/{id}/show/metadata`, which answers the live
 * show and 404s when there is none. That read happens only with analytics on,
 * after the action itself has succeeded.
 *
 * Only IDs, counts and flags: never a song title, note or any free text.
 */

const toId = (value) => {
  const n = Number(value);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
};

/**
 * `{ show_live: true, show_id }` while the band has a live show, `{ show_live:
 * false }` when it has none, and `{}` when that could not be read.
 */
export const fetchShowContext = async (fetchWrapper, bandId) => {
  try {
    const metadata = await fetchWrapper.get(`${config.apiBaseUrl}/public/bands/${bandId}/show/metadata`);
    const showId = toId(metadata?.showId);
    return showId ? { show_live: true, show_id: showId } : { show_live: false };
  } catch (err) {
    return err?.status === 404 ? { show_live: false } : {};
  }
};

/**
 * Sends a Performer event once its action has succeeded. `show` is the show
 * context when the caller knows it; without one it is read with
 * `fetchWrapper`. With analytics off nothing is read or sent, and it never
 * throws.
 */
export const capturePerformerEvent = async (name, { bandId, fetchWrapper = null, show = null, ...properties } = {}) => {
  if (!analyticsEnabled()) return;
  try {
    const context = show ?? (fetchWrapper ? await fetchShowContext(fetchWrapper, bandId) : {});
    await captureEvent(name, { band_id: toId(bandId), ...context, ...properties });
  } catch (_e) {
    // Analytics must never break the page.
  }
};

// ---------------------------------------------------------------------------
// Boosts on queue entries, for `queue_song_completed`
//
// The queue the dashboard reads says how much is on an entry
// (`donation_amount`, a paid request's price and its boosts together) and,
// until the entry starts playing, the vibe of its last boost
// (`boosted_vibe_id`, ENG-866). Neither is left on the entry by the time it is
// marked played, so the dashboard notes what it saw while the entry waited,
// plus every `boost_alert` frame, kept in sessionStorage per band so a reload
// mid-show keeps it:
//
// - `boosted`: a boost was seen on it;
// - `firstCents` / `firstBoosted`: its amount the first time it was seen, and
//   whether it was already boosted then (a paid request's price when not);
// - `boostCents`: the sum of the boosts announced by `boost_alert`.
// ---------------------------------------------------------------------------

const STORE_PREFIX = 'songshake.queueBoosts.';
const memoryStore = new Map();

const readStore = (bandId) => {
  const key = `${STORE_PREFIX}${bandId}`;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch {
    // Storage blocked: the in-memory copy below.
  }
  return memoryStore.get(key) ?? {};
};

const writeStore = (bandId, entries) => {
  const key = `${STORE_PREFIX}${bandId}`;
  memoryStore.set(key, entries);
  try {
    window.sessionStorage.setItem(key, JSON.stringify(entries));
  } catch {
    // The in-memory copy still serves this page.
  }
};

const toCents = (dollars) => {
  const n = Number(dollars);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
};

/**
 * Notes the queue as the dashboard read it: `[{ id, status, donationAmount,
 * boostedVibeId }]`. Entries no longer in the queue are forgotten.
 */
export const noteQueueEntries = (bandId, entries) => {
  if (!analyticsEnabled() || !Array.isArray(entries)) return;
  const before = readStore(bandId);
  const after = {};
  for (const entry of entries) {
    const id = toId(entry?.id);
    if (!id) continue;
    const record = { ...(before[id] ?? {}) };
    const boosted = Boolean(entry.boostedVibeId);
    if (record.firstCents == null) {
      record.firstCents = toCents(entry.donationAmount);
      record.firstBoosted = boosted;
    }
    if (boosted) record.boosted = true;
    after[id] = record;
  }
  writeStore(bandId, after);
};

/** Notes a `boost_alert` frame (`{ entryId, amountCents }`). */
export const noteBoostAlert = (bandId, frame) => {
  if (!analyticsEnabled()) return;
  const id = toId(frame?.entryId);
  if (!id) return;
  const entries = readStore(bandId);
  const record = { ...(entries[id] ?? {}) };
  const amount = Number(frame?.amountCents);
  record.boosted = true;
  if (Number.isSafeInteger(amount) && amount > 0) record.boostCents = (record.boostCents ?? 0) + amount;
  entries[id] = record;
  writeStore(bandId, entries);
};

/**
 * The Boost and paid-request properties of `queue_song_completed` for a queue
 * entry (`{ id, position, donationAmount, boostedVibeId }`).
 */
export const completionProperties = (bandId, entry) => {
  const id = toId(entry?.id);
  const record = (id && readStore(bandId)[id]) || {};
  const totalCents = toCents(entry?.donationAmount);
  const wasBoosted = Boolean(record.boosted || entry?.boostedVibeId);

  let boostCents = null;
  if (record.boostCents > 0) {
    boostCents = record.boostCents;
  } else if (wasBoosted && record.firstCents != null && !record.firstBoosted && totalCents > record.firstCents) {
    boostCents = totalCents - record.firstCents;
  }

  let wasPaidRequest;
  if (record.firstCents != null && !record.firstBoosted) {
    wasPaidRequest = record.firstCents > 0;
  } else if (!wasBoosted) {
    wasPaidRequest = totalCents > 0;
  } else {
    wasPaidRequest = boostCents != null && totalCents > boostCents;
  }

  const properties = {
    queued_song_id: id,
    was_boosted: wasBoosted,
    was_paid_request: wasPaidRequest,
    position_in_queue: Number.isFinite(Number(entry?.position)) ? Number(entry.position) : null,
  };
  if (wasBoosted && boostCents != null) properties.boost_total_cents = boostCents;
  return properties;
};

/** For tests. */
export const _resetPerformerEventsForTests = () => {
  memoryStore.clear();
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith(STORE_PREFIX)) window.sessionStorage.removeItem(key);
    }
  } catch {
    // Nothing stored.
  }
};
